import { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import { Info, Loader2 } from 'lucide-react';

import AdminLayout from '../../components/AdminLayout';
import CentralityMap from '../../components/admin/CentralityMap';
import CentralityDataPanel from '../../components/admin/CentralityDataPanel';
import type { ArtistCentralityData } from '../../components/admin/CentralityMap';

const GET_CENTRALITY = gql`
  query GetGeographicCentrality($radiusKm: Float) {
    adminGeographicCentrality(radiusKm: $radiusKm) {
      profile {
        id
        userId
        displayName
        slug
        city
        region
        country
        countryCode
        avatarUrl
      }
      latitude
      longitude
      neighborCount
      nearestNeighborKm
    }
  }
`;

function GeographicCentrality() {
  const { t } = useTranslation('admin');
  const [radiusKm, setRadiusKm] = useState('100');
  const [countryFilter, setCountryFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'scatter' | 'hexbin'>('scatter');
  const [selectedArtist, setSelectedArtist] = useState<ArtistCentralityData | null>(null);

  const { data, loading } = useQuery(GET_CENTRALITY, {
    variables: { radiusKm: Number(radiusKm) },
  });

  const artists: ArtistCentralityData[] = data?.adminGeographicCentrality ?? [];

  const countries = useMemo(() => {
    const codes = new Map<string, string>();
    for (const a of artists) {
      if (a.profile.countryCode && a.profile.country) {
        codes.set(a.profile.countryCode, a.profile.country);
      }
    }
    return [...codes.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [artists]);

  const filtered = useMemo(
    () => (countryFilter === 'all' ? artists : artists.filter((a) => a.profile.countryCode === countryFilter)),
    [artists, countryFilter]
  );

  return (
    <AdminLayout
      title={t('geographic-centrality.page-title')}
      description={t('geographic-centrality.page-description')}
    >
      <div className="flex items-center gap-4 pb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Radius</span>
          <Tabs value={radiusKm} onValueChange={setRadiusKm}>
            <TabsList>
              {['25', '50', '100', '250'].map((v) => (
                <TabsTrigger key={v} value={v} className="text-xs">
                  {v} km
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center gap-2">
          <TooltipProvider delayDuration={300}>
            <span className="flex items-center gap-1 text-xs font-medium text-zinc-500">
              {t('geographic-centrality.country-label')}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex rounded-full text-zinc-400 outline-offset-2 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-zinc-400"
                    aria-label={t('geographic-centrality.country-filter-info-aria')}
                  >
                    <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm text-xs leading-snug">
                  {t('geographic-centrality.country-filter-tooltip')}
                </TooltipContent>
              </Tooltip>
            </span>
          </TooltipProvider>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue placeholder="All Countries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {countries.map(([code, name]) => (
                <SelectItem key={code} value={code}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">View</span>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'scatter' | 'hexbin')}>
            <TabsList>
              <TabsTrigger value="scatter" className="text-xs">
                Scatter
              </TabsTrigger>
              <TabsTrigger value="hexbin" className="text-xs">
                Hexbin
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
      </div>

      <p className="pb-3 text-xs text-zinc-500">{t('geographic-centrality.country-filter-note')}</p>

      <div className="-mx-8 flex" style={{ height: 'calc(100dvh - 300px)' }}>
        <div className="flex-1">
          <CentralityMap
            data={filtered}
            viewMode={viewMode}
            onArtistClick={setSelectedArtist}
            selectedArtist={selectedArtist}
          />
        </div>
        <CentralityDataPanel data={filtered} onArtistClick={setSelectedArtist} selectedArtist={selectedArtist} />
      </div>
    </AdminLayout>
  );
}

export default GeographicCentrality;
